<template>
  <el-col :span="24" ref="trxContainer">
    <el-row type="flex" justify="start" align="middle" class="v-padded-s trx-title-container" ref="trxTitleContainer">
      <el-col :span="isSaving && isSaveable ? 6 : 12" class="h-padded left-aligned-col">
        <el-row type="flex" align="middle" justify="start">
          <h2 style="margin: 0; font-weight: 100; display: inline-block;">ADD PURCHASE ORDER</h2>
        </el-row>
      </el-col>
      <el-col :span="12" v-if="isSaving && isSaveable">
        <TransactionProgressBar
          :totalRequests="totalRequests"
          :completedRequests="completedRequests"
          :error="(!!failedLines.length)"
        />
      </el-col>
      <DataEntryButtons
        :isSaveable="isSaveable"
        :isOpenable="isOpenable"
        :isBalanced="transactionIsBalanced"
        :isSaving="isSaving"
        :transactionType="transactionType"
        :hasPermission="hasPermission"
        @goBack="goBack"
        @submit="submitForm"
        @dropdownClick="handleDropdownClick"
      />
    </el-row>
    <el-row type="flex" ref="mockTrxTitleContainer" />
    <el-row justify="start" align="start" type="flex" class="trx-header" ref="trxHeaderContainer">
      <el-col :span="8" style="margin-right: 10px">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Vendor"
            :fields="fields.vendor"
            :form="form"
            :idPrefix="'a-'"
            :nonAsyncSuggestions.sync="nonAsyncSuggestions"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formChanged="updateForm"
          />
        </el-row>
        <el-row justify="start" align="start" type="flex">
          <HeaderFields
            title="Batch"
            :fields="fields.batch"
            :form="form"
            :idPrefix="'b-'"
            :nonAsyncSuggestions.sync="nonAsyncSuggestions"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formChanged="updateForm"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin: 0 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="PO Data"
            :fields="fields.poData"
            :form="form"
            :idPrefix="'c-'"
            :nonAsyncSuggestions.sync="nonAsyncSuggestions"
            :modalOpen="dialogVisible"
            :isSaving="isSaving"
            @formChanged="updateForm"
            :key="effectiveDateMandatory"
          />
        </el-row>
      </el-col>
      <el-col :span="8" style="margin-left: 10px;">
        <el-row justify="start" align="middle" type="flex">
          <HeaderFields
            title="Transaction Info"
            :fields="fields.transactionInfo"
            :lines="lines"
            :idPrefix="'d-'"
            :modalOpen="dialogVisible"
          />
        </el-row>
      </el-col>
    </el-row>
    <el-row v-if="lines" justify="start" align="middle" type="flex" id="trx-lines-container">
      <DistributionLines
        v-if="dataLoaded"
        entityType="po"
        ref="distribution-lines"
        :fields="lineFields"
        :existingLines="lines"
        :initLines="20"
        :modalOpen="dialogVisible"
        :parentForm="form"
        :reset="resetLines"
        :readOnly="isSaving"
        :hasEditPermission="hasEditPermission"
        @lineChanged="trackLineChanges"
        @linesHaveBeenChanged="setLinesHaveBeenChanged"
        @formatMoneyFields="formatLineMoneyField"
        @hasBeenReset="linesHaveBeenReset"
        @updateLines="updateLines"
        @mounted="setBatchAttributeLineValues"
      />
      <LinesLoading v-else :distLinesHeight="distLinesHeight" />
    </el-row>
    <el-dialog
      title="Confirm cancellation"
      :visible.sync="dialogVisible"
      size="tiny"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <span>Do you wish to save this transaction as a draft?</span>
      <span slot="footer" class="dialog-footer">
        <el-button
          class="exit-modal-btn"
          :disabled="isSaving"
          @click="cancelExit">
          Cancel
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          @click="goBack(true)">
          No
        </el-button>
        <el-button
          class="exit-modal-btn"
          type="primary"
          :disabled="isSaving"
          :loading="isSaving"
          @click="submitForm(false, null, true)">
          Yes
        </el-button>
      </span>
    </el-dialog>
    <RouteChangeDialog :visible="showExitWarningDialog" @isVisible="handleExitWarningDialogVisible" @confirm="goBack(true)" />
    <NewBatch :hasCreatePermission="userCanCreateBatch" @populateFieldWithNewModel="populateFieldWithNewModel" />
    <CustomerVendorForm :type="customerVendorFormType" :modelId="modelId" @updateModelIdProp="updateVendorModelId" @populateFieldWithNewModel="populateFieldWithNewModel" />
  </el-col>
</template>

<script>
  /* eslint-disable camelcase */
  import { mapGetters } from 'vuex'
  import DistributionLines from '@/components/DistributionLinesNew1.vue'
  import HeaderFields from '@/components//HeaderFields.vue'
  import NewBatch from '@/components/RouteComponents/Batches/NewBatch.vue'
  import CustomerVendorForm from '@/components/FormComponents/CustomerVendorForm.vue'
  import DataEntryButtons from '@/components/DataEntryButtons.vue'
  import RouteChangeDialog from '@/components/RouteChangeDialog.vue'
  import LinesLoading from '@/components/LinesLoading.vue'
  import autoNumber from '@/utils/helpers/DataEntryViews/AutoNumber'
  import { transactionMixins } from '@/Mixins/Transactions'
  import { transactionCreateViewMixins } from '@/Mixins/TransactionCreateViews'
  import { poFieldsMixin } from '@/Mixins/PoFields'
  import { vendorMixins } from '@/Mixins/VendorMixins'
  import { formatDistributionLineMoneyFields } from '@/utils/helpers/DataEntryViews/FormatMoneyFields'
  import { tabThruModalButtons } from '@/utils/helpers/KeyNavHelper'
  import TransactionProgressBar from '@/components/TransactionProgressBar.vue'
  import { getSimpleDate } from '@/utils/helpers/FormatDate'

  export default {
    components: {
      DistributionLines,
      HeaderFields,
      NewBatch,
      CustomerVendorForm,
      DataEntryButtons,
      RouteChangeDialog,
      TransactionProgressBar,
      LinesLoading
    },
    mixins: [ transactionMixins, transactionCreateViewMixins, poFieldsMixin, vendorMixins ],
    data () {
      const batch = this.setFormBatch()
      return {
        modelId: null,
        transactionType: { name: 'po', path: '/pos/', prettyName: 'Purchase Order' },
        form: {
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          description: '',
          period: '',
          date: getSimpleDate(new Date(), 'yyyy-mm-dd'),
          number: autoNumber() || '',
          amount_decimal: '',
          vendor: { label: '', value: '' },
          state: '',
          effective_date: '',
          expiration: '',
          lines: []
        },
        effectiveDateMandatory: false,
        nonAsyncSuggestions: { active: '', period: [{ label: 'Next', value: '' }] },
        exitWarningDialogShown: false,
        showExitWarningDialog: false,
        currentVendorSelection: '',
        autoEnterLineLimit: 3 // when adding default values like account, tax code
      }
    },
    methods: {
      /**
       * Updates modelId prop passed to customer create/edit modal
       */
      updateVendorModelId (id) {
        this.modelId = id
      },
      /**
       * Generates an alternate route depending on whether to the to argument contains a route
       * name and if it contains a query.
       * @param to
       * @return {Object}
       */
      generateAltRoute (to) {
        if (to && to.name && to.query) {
          return { name: to.name, query: to.query }
        }
        return { name: to.name }
      },
      /**
       * Updates the header form
       */
      updateForm (form, changedField = null) {
        this.form = { ...form }
        if (this.form.vendor.value) {
          this.handleVendorChange(form)
          this.currentVendorSelection = this.form.vendor.value
        } else {
          this.currentVendorSelection = ''
        }
        this.trackHeaderChanges()
        this.trackNumberChange()
        this.isSaveable = this.checkIfCanBeSaved()

        // Update first dist line if necessary
        if (changedField === 'amount_decimal') {
          this.setFirstLineAmount(form[changedField])
        } else if (changedField === 'batch') {
          this.setBatchAttributeLineValues(this.batchAttributes)
        }
      },
      async handleVendorChange (form) {
        if (form.vendor.value) {
          let vendorData = this.getVendorById(form.vendor.value)
          if (!vendorData.id) {
            await this.$store.dispatch('fetchVendorById', form.vendor.value)
            vendorData = this.getVendorById(form.vendor.value)
            if (vendorData && vendorData.address_selected && vendorData.address_selected.id && vendorData.address_selected.city) {
              await this.$store.dispatch('fetchCitiesWithQuery', { id__in: [vendorData.address_selected.city], page_size: 1 })
            }
          } else {
            if (vendorData && vendorData.address_selected && vendorData.address_selected.id && vendorData.address_selected.city && typeof vendorData.address_selected.city === 'number') {
              await this.$store.dispatch('fetchCitiesWithQuery', { id__in: [vendorData.address_selected.city], page_size: 1 })
            }
          }
        }
      },
      /**
       * Updates distribution lines
       * @param lines
       */
      updateLines (lines) {
        this.lines = lines
        this.isSaveable = this.checkIfCanBeSaved()
        this.isOpenable = !!this.transactionIsBalanced && this.checkIfCanBeSaved()
      },
      /**
       * Resets the component's internal state
       */
      resetForm () {
        const batch = this.setFormBatch(this.form.batch.value)
        const form = {                                  // Reset the form
          batch: { label: batch ? batch.name : '', value: batch ? batch.id : '' },
          vendor: { label: '', value: '' },
          description: '',
          period: { label: '', value: '' },
          date: getSimpleDate(new Date(), 'yyyy-mm-dd'),
          number: autoNumber() || '',
          amount_decimal: '',
          state: '',
          effective_date: this.effectiveDateMandatory ? getSimpleDate(new Date(), 'yyyy-mm-dd') : '',
          expiration: ''
        }
        this.form = { ...form }
        this.nonAsyncSuggestions = {
          active: '',
          period: [
            { label: 'Next', value: '', selected: true }
          ]
        }
      },
      /**
       * Cancels exit if the exit confirmation dialog is showing and the user hits "Cancel"
       */
      cancelExit () {
        this.altRoute = null
        this.handleDialogVisible(false)
      },
      /**
       * Formats dollar amounts for a specific distribution line field
       * @param coordinates
       */
      formatLineMoneyField (coordinates) {
        formatDistributionLineMoneyFields(this, coordinates)
      },
      /**
       * Governs visibility of save confirmation modal when cancelling transaction creation
       * @param visible
       */
      handleDialogVisible (visible) {
        this.dialogVisible = visible
        tabThruModalButtons(visible ? 'exit-modal-btn' : null)
      },
      /**
       * Governs visibility of the exit confirmation modal that displays when the user hits the browser back
       * button while a transaction is saving
       * @param visible
       */
      handleExitWarningDialogVisible (visible) {
        this.showExitWarningDialog = visible
        this.exitWarningDialogShown = visible
      },
      /**
       * Tracks changes to the transaction number
       */
      trackNumberChange () {
        this.vendorNumberVerifier = `${this.form.number}`
      }
    },
    computed: {
      ...mapGetters({
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions',
        checkIfUserHasAnyOfThePermissions: 'checkIfUserHasAnyOfThePermissions'
      }),
      states () {
        return this.$store.getters.getAllStates.map(v => ({ label: v.name, value: v.id }))
      },
      currency () {
        return this.$store.getters.getCurrencyByBatchId(this.form.batch.value)
      },
      hasCreatePermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_purchaseorder', 'entry.add_purchaseorder'])
      },
      hasPostPermission () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_purchaseorder']) && this.checkIfUserHasAnyOfThePermissions(['entry.post_purchaseorder', 'auth.post_transactions'])
      },
      hasEditPermission () {
        if (!this.hasCreatePermission) return false
        else return true
      },
      userCanCreateBatch () {
        return this.checkIfUserHasRequiredPermissions(['entry.view_batch', 'entry.add_batch'])
      },
      hasPermission () {
        return {
          create: this.hasCreatePermission,
          edit: this.hasEditPermission,
          delete: true,
          post: this.hasPostPermission
        }
      },
      fields () {
        const { currency } = this
        let fields = {
          batch: {
            inputFields: [
              {
                name: 'batch',
                label: 'Batch Name',
                dataType: 'number',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'batches' },
                required: true,
                triggerOnFocus: false,
                msg: 'Please select a batch',
                addBtn: this.userCanCreateBatch
              }
            ],
            displayFields: [
              {
                name: 'batch',
                label: 'Company',
                value: this.batchAttributes.company ? this.batchAttributes.company.name : ''
              },
              {
                name: 'batch',
                label: 'Production',
                value: this.batchAttributes.production ? this.batchAttributes.production.name : ''
              },
              {
                name: 'batch',
                label: 'Bank',
                value: this.batchAttributes.bank ? this.batchAttributes.bank.name : ''
              },
              {
                name: 'batch',
                label: 'Currency',
                value: this.batchAttributes.currency ? this.batchAttributes.currency.code : ''
              }
            ]
          },
          vendor: {
            inputFields: [
              {
                name: 'vendor',
                label: 'Vendor',
                dataType: 'string',
                fieldType: 'input',
                autocomplete: true,
                searchAsync: true,
                queryData: { endpoint: 'vendors', params: { active: 'True' } },
                required: true,
                triggerOnFocus: false,
                msg: 'Please select a vendor',
                addBtn: this.userCanCreateVendor
              }
            ],
            displayFields: [
              {
                name: 'vendor',
                label: 'Address',
                value: this.vendorAddressLine || ''
              },
              {
                name: 'vendor',
                label: 'Type',
                value: this.form.vendor.value ? this.getVendorById(this.form.vendor.value).entity_type : ''
              }
            ]
          },
          poData: {
            inputFields: [
              {
                name: 'description',
                label: 'Description',
                dataType: 'string',
                fieldType: 'input',
                required: true,
                msg: 'Please provide a description',
                min: 3
              },
              {
                name: 'number',
                label: 'Number',
                dataType: 'string',
                fieldType: 'input',
                required: true
              },
              {
                name: 'amount_decimal',
                label: 'Amount',
                dataType: 'string',
                fieldType: 'money',
                required: true,
                isMoney: true
              },
              {
                name: 'date',
                label: 'Date',
                dataType: 'string',
                fieldType: 'date',
                required: true,
                msg: 'Please specify a date'
              },
              {
                name: 'effective_date',
                label: 'Effective Date',
                dataType: 'string',
                required: this.effectiveDateMandatory,
                fieldType: 'date'
              },
              {
                name: 'expiration',
                label: 'Expiration Date',
                dataType: 'string',
                fieldType: 'date'
              },
              {
                label: 'State',
                name: 'state',
                placeholder: 'Select a state...',
                dataType: 'number',
                fieldType: 'select',
                remote: false,
                filterable: true,
                options: this.states
              },
              {
                name: 'period',
                label: 'Period',
                dataType: 'number',
                fieldType: 'select',
                options: [
                  { label: 'Next', value: '', selected: !(this.currentPeriod && this.currentPeriod.id) }
                ]
              }
            ]
          },
          transactionInfo: {
            existingModelFields: [
              {
                label: 'Date created',
                value: this.formatDateToLocale(new Date())
              },
              {
                label: 'Status',
                value: 'NEW'
              },
              {
                label: 'Remaining',
                value: ''
              },
              {
                name: 'runningTotal',
                label: 'Relieved',
                prefix: currency ? currency.symbol : ''
              }
            ]
          }
        }
        if (this.currentPeriod && this.currentPeriod.id) {
          fields.poData.inputFields[7].options.push({
            label: 'Current',
            value: this.currentPeriod.id,
            selected: true
          })
        }
        return fields
      }
    },
    watch: {
      currentVendorSelection () {
        if (this.currentVendorSelection) {
          this.setDefaultState()
          this.setDefaultTaxcodeLineValues()
          this.setDefaultAccountLineValues()
        }
      },
      vendorNumberVerifier () {
        this.trxAlreadyExists = false
        if (this.trxAlreadyExistsMsg) {
          this.trxAlreadyExistsMsg.close()
        }
        clearTimeout(this.vendorNumberCheckTimeout)
        this.vendorNumberCheckTimeout = setTimeout(() => this.checkIfTrxExists(), 1000)
      }
    },
    created () {
      this.transactionIsBalanced = true
    },
    beforeDestroy () {
      this.$store.dispatch('clearHeaderFormState')
    }
  }
</script>

<style scoped>
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding-top: 12px;
    padding-bottom: 12px;
  }
  .left-aligned-col {
    text-align: left;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
  .el-form-item__label {
    padding: 20px !important;
    border: 1px solid pink;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Pos/New.vue